import React, { useState } from "react";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import { useNavigate, useParams } from "react-router-dom";
import useForm from "../../hooks/useForm";
import CustomInput from "../../components/Form/Input";
import ImageUpload from "../../components/Form/ImageUpload";
import CustomButton from "../../components/Form/CustomButton";
import { useThunk } from "../../hooks/useThunk";
import { createRequestRevision } from "../../store";
import showToast from "../../utils/toastNotifications";
import { firebaseUplaodFolders, referencesFormats } from "../../constants/commonConstants";
import useFirebaseFileUpload from "../../hooks/useFirebaseFileUpload";
import { isValidObjectId } from "../../utils/validationUtils";
import ErrorDisplay from "../../error/ErrorDisplay";

const RequestRevision = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    clearError,
    validateForm,
    setTouched,
    setFormData,
  } = useForm(
    {
      revisionNote: "",
    },
    {
      revisionNote: true,
    },
    {
      revisionNote: COMMON_FIELD_TYPES.REQUIRED_FIELD,
    }
  );

  //Call to Thunk
  const [doRequestRevision, isRequestRevision, requestRevisionError] = useThunk(
    createRequestRevision
  );

  const [
    doUploadRefrencesToFirebase,
    isUploadRefrencesToFirebaseLoading,
    uploadRefrencesToFirebaseError,
  ] = useFirebaseFileUpload();

  const [uploadFiles, setUploadFiles] = useState([]);

  const onFileUpload = (files) => {
    setUploadFiles([...files]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setTouched({
      revisionNote: true,
    });

    if (!validateForm()) {
      console.log("Validation Failed");
      return;
    } else {
      console.log("Success");
    }

    let documents = [];
    // Upload files to firebase
    if (uploadFiles.length > 0) {
      const result = await doUploadRefrencesToFirebase(
        uploadFiles,
        firebaseUplaodFolders.F_COMPLAINTS
      );
      if (result?.success) {
        console.log("Files uploaded successfully", result.uploadedUrls);
        // Set the uploaded files to the form data
        documents = result.uploadedUrls;
      } else {
        console.log("Error uploading files", result.error);
        showToast("error", "Error uploading files");
        return;
      }
    }

    const result = await doRequestRevision({
      orderId: orderId,
      revisionNote: formData?.revisionNote || "",
      documents: documents.length > 0 ? documents : null,
    });

    if (result?.success) {
      showToast("success", "Revision Request successfully");
      setTimeout(() => {
        navigate("/client/order-history");
      }, 2000);
    } else {
      showToast("error", result?.error?.message);
      navigate("/client/order-history");
    }
  };

  return (
    <div className="px-5 sm:px-10 lg:px-[6.25rem] mb-[5.5rem]">
      <div className="pt-6 text-left lg:text-center lg:pt-12">
        <h1 className="text-level-2 lg:text-level-1 text-primary font-nonito_sans font-bold font-style:normal tracking-[0.0026rem]">
          Request Revision
        </h1>
        <p className="font-inter text-base font-normal text-[#535862]">
          Request Revision for Delivered Task
        </p>
      </div>
      {
        // Show ErrorDisplay if orderId is invalid or there is an error
        !isValidObjectId(orderId) || requestRevisionError ? (
          <ErrorDisplay
            errorMessage={`${
              requestRevisionError?.message || "Invalid Order ID"
            }`}
            buttonText="Back to Order History"
            onButtonClick={() => navigate("/client/order-history")}
          />
        ) : (
          <div className="mt-7 lg:mt-[40px] mb-16 lg:mb-[88px] p-5 lg:p-9 border border-[#CBD5E0] rounded-[20px] bg-white">
            <div className="flex flex-col gap-6 mb-4 ">
              <CustomInput
                label="Revision Note"
                placeholder="Describe Your Revision Request"
                outerContainerStyle="flex-grow"
                value={formData.revisionNote}
                onChange={(e) =>
                  handleInputChange("revisionNote", e.target.value)
                }
                onBlur={(e) => handleBlur("revisionNote", e.target.value)}
                name="revisionNote"
                error={touched.revisionNote && errors.revisionNote}
                isRequired={true}
              />

              <ImageUpload
                label="Additional Requirements"
                accept={[referencesFormats.JPEG_JPG, referencesFormats.PNG]}
                onFileUpload={onFileUpload}
                uploadFiles={uploadFiles}
                isDisabled={isUploadRefrencesToFirebaseLoading}
                isLoading={isUploadRefrencesToFirebaseLoading}
                uploadText="Jpeg, Jpg, png, files are allowed"
                numberOfFiles={5}
              />
            </div>

            <hr className="mt-12" />
            <div className="flex justify-end mt-12">
              <CustomButton
                buttonText="Submit"
                bgColor="bg-primary"
                textColor="text-white"
                borderColor="border-transparent"
                loading={
                  isRequestRevision || isUploadRefrencesToFirebaseLoading
                }
                loaderColor="white"
                disabled={
                  isRequestRevision || isUploadRefrencesToFirebaseLoading
                }
                onClick={handleSubmit}
                className="w-[120px] h-[46px] px-4 py-4 font-inter text-level-6 text-white rounded-xl gap-4"
              />
              <button
                type="button"
                className="ml-9 font-inter text-level-6 text-primary font-semibold leading-[22px] 
                        not-italic"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
            </div>
          </div>
        )
      }
    </div>
  );
};

export default RequestRevision;
